<template>
  <dd-modal
    v-model:visible="visible"
    :subtitle="subtitle"
    :extend-height="true"
    title="Import Tasks from CSV"
    desktop-sizing="large"
    class="csv-import-modal"
    @handle-close="handleClose"
  >
    <!-- Step 1: Select file -->
    <div v-if="currentStep === 'select'">
      <div class="csv-import-modal__format-info">
        <h4>CSV Format</h4>
        <p>
          Your CSV file must include a header row with column names.
          Column order does not matter.
        </p>
        <div class="csv-import-modal__columns">
          <div class="csv-import-modal__column-group">
            <strong>Required columns:</strong>
            <ul>
              <li><code>title</code> &mdash; Task title</li>
              <li><code>status</code> &mdash; Status name (must match a status in this project)</li>
              <li><code>priority</code> &mdash; Priority name (e.g. "High", "Medium", "Low")</li>
            </ul>
          </div>
          <div class="csv-import-modal__column-group">
            <strong>Optional columns:</strong>
            <ul>
              <li><code>description</code> &mdash; Task description</li>
              <li><code>assignee</code> &mdash; Assignee email address</li>
              <li><code>due_date</code> &mdash; Due date in YYYY-MM-DD, MM/DD/YYYY, or MMM D, YYYY format</li>
              <li><code>tags</code> &mdash; Comma-separated tag names</li>
            </ul>
          </div>
          <div v-if="editableCustomFields.length > 0" class="csv-import-modal__column-group">
            <strong>Custom field columns:</strong>
            <ul>
              <li v-for="field in editableCustomFields" :key="field.id">
                <code>{{ field.name }}</code> &mdash; {{ describeCustomFieldFormat(field) }}
              </li>
            </ul>
          </div>
        </div>
        <dd-button
          :is-loading="isPreparingExample"
          type="text"
          class="csv-import-modal__download-example"
          @click="downloadExampleCsv"
        >
          <i class="ddi ddi-download" /> Download Example CSV
        </dd-button>
      </div>

      <div class="csv-import-modal__file-input-area">
        <label class="csv-import-modal__file-label">
          <i class="ddi ddi-data-transfer" />
          <span v-if="!selectedFile">Click to select a CSV file</span>
          <span v-else>{{ selectedFile.name }}</span>
          <input
            ref="fileInput"
            :key="fileInputKey"
            type="file"
            accept=".csv"
            class="csv-import-modal__file-hidden"
            @change="handleFileSelected"
          >
        </label>
      </div>

      <!-- Parse errors -->
      <div v-if="structureErrors.length > 0" class="csv-import-modal__errors">
        <p class="csv-import-modal__error-title">
          <i class="ddi ddi-error" /> CSV format errors:
        </p>
        <ul>
          <li v-for="(err, idx) in structureErrors" :key="idx">{{ err }}</li>
        </ul>
      </div>
    </div>

    <!-- Step 2: Preview -->
    <div v-if="currentStep === 'preview'">
      <div class="csv-import-modal__summary">
        <p class="csv-import-modal__summary-success">
          <i class="ddi ddi-circle-success" />
          {{ validRows.length }} task{{ validRows.length !== 1 ? 's' : '' }} ready to import
        </p>
        <p v-if="parseErrors.length > 0" class="csv-import-modal__summary-warning">
          <i class="ddi ddi-warning" />
          {{ parseErrors.length }} row error{{ parseErrors.length !== 1 ? 's' : '' }} &mdash; these rows will be skipped
          <a href="#" class="just-a-link" @click.prevent="showErrorDetails = !showErrorDetails">
            {{ showErrorDetails ? 'Hide details' : 'Show details' }}
          </a>
        </p>
        <div v-if="showErrorDetails && parseErrors.length > 0" class="csv-import-modal__error-details">
          <ul>
            <li v-for="(err, idx) in parseErrors" :key="idx">
              Row {{ err.row }}: {{ err.field }} &mdash; {{ err.message }}
            </li>
          </ul>
        </div>
      </div>

      <div v-if="parseWarnings.length > 0" class="csv-import-modal__warnings">
        <p v-for="(warn, idx) in parseWarnings" :key="idx">{{ warn }}</p>
      </div>

      <!-- Preview table -->
      <div class="csv-import-modal__preview-table-wrapper">
        <table class="csv-import-modal__preview-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Assignee</th>
              <th>Due Date</th>
              <th v-for="column in customFieldColumns" :key="column.field.id">{{ column.field.name }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in previewRows" :key="idx">
              <td>{{ row.title }}</td>
              <td>{{ row.status }}</td>
              <td>{{ row.priority }}</td>
              <td>{{ row.assignee || '—' }}</td>
              <td>{{ row.dueDate || '—' }}</td>
              <td v-for="column in customFieldColumns" :key="column.field.id">
                {{ customFieldDisplayText(row, column.field) || '—' }}
              </td>
            </tr>
          </tbody>
        </table>
        <p v-if="validRows.length > 5" class="csv-import-modal__preview-more">
          ...and {{ validRows.length - 5 }} more
        </p>
      </div>
    </div>

    <!-- Step 3: Importing -->
    <div v-if="currentStep === 'importing'" class="csv-import-modal__importing">
      <p class="csv-import-modal__importing-text">
        Importing task {{ importProgress }} of {{ importTotal }}...
      </p>
      <div class="csv-import-modal__progress-bar">
        <div
          :style="{ width: progressPercent + '%' }"
          class="csv-import-modal__progress-fill"
        />
      </div>
      <p class="csv-import-modal__importing-subtext">
        Please do not close this dialog.
      </p>
    </div>

    <!-- Step 4: Results -->
    <div v-if="currentStep === 'results'" class="csv-import-modal__results">
      <div class="csv-import-modal__results-summary">
        <p v-if="importSuccessCount > 0" class="csv-import-modal__summary-success">
          <i class="ddi ddi-circle-success" />
          {{ importSuccessCount }} task{{ importSuccessCount !== 1 ? 's' : '' }} imported successfully
        </p>
        <p v-if="importFailCount > 0" class="csv-import-modal__summary-error">
          <i class="ddi ddi-circle-error" />
          {{ importFailCount }} task{{ importFailCount !== 1 ? 's' : '' }} failed to import
          <a href="#" class="just-a-link" @click.prevent="showImportErrorDetails = !showImportErrorDetails">
            {{ showImportErrorDetails ? 'Hide details' : 'Show details' }}
          </a>
        </p>
        <div v-if="showImportErrorDetails && importErrors.length > 0" class="csv-import-modal__error-details">
          <ul>
            <li v-for="(err, idx) in importErrors" :key="idx">
              "{{ err.title }}" &mdash; {{ err.error }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <span>
        <!-- Select step -->
        <template v-if="currentStep === 'select'">
          <dd-button type="text" @click="handleClose">Cancel</dd-button>
        </template>

        <!-- Preview step -->
        <template v-if="currentStep === 'preview'">
          <dd-button type="text" @click="goBackToSelect">Back</dd-button>
          <dd-button type="text" @click="handleClose">Cancel</dd-button>
          <dd-button
            :is-disabled="validRows.length === 0"
            color="light-orange"
            @click="startImport"
          >Import {{ validRows.length }} Task{{ validRows.length !== 1 ? 's' : '' }}</dd-button>
        </template>

        <!-- Results step -->
        <template v-if="currentStep === 'results'">
          <dd-button color="light-orange" @click="handleDone">Done</dd-button>
        </template>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Helpers */
import CsvImportAssistant, {
  ICsvTaskRow,
  ICsvRowError,
  ICsvImportOptions,
  ICsvCustomFieldColumn,
} from '@/helpers/csv-import-assistant'
import CustomFieldAssistant, {
  ECustomFieldPermissions,
} from '@/helpers/custom-field-assistant'

/* Data types */
import {
  ICustomField,
  ICustomFieldValue,
} from '@/api/types/response.types'

/* API */
import TasksAPI from '@/api/modules/tasks.api'

export default defineComponent({
  name: 'CsvImportModal',

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: Number,
      required: true,
    },
    projectName: {
      type: String,
      default: '',
    },
  },

  emits: ['close', 'import-completed', 'update:showModal'],

  data() {
    return {
      currentStep: 'select' as 'select' | 'preview' | 'importing' | 'results',

      // File selection
      selectedFile: null as File | null,
      fileInputKey: 0,
      structureErrors: [] as string[],

      // Project options for resolving names to IDs, loaded each time the modal opens
      importOptions: null as ICsvImportOptions | null,
      importOptionsRequest: null as Promise<void> | null,
      isPreparingExample: false,

      // Async work checks these before publishing, so results meant for an earlier opening or file are dropped.
      // openingID changes when the modal opens or closes; fileReadID also changes for each picked file and on Back.
      openingID: 0,
      fileReadID: 0,

      // Parse results
      customFieldColumns: [] as ICsvCustomFieldColumn[],
      validRows: [] as ICsvTaskRow[],
      parseErrors: [] as ICsvRowError[],
      parseWarnings: [] as string[],
      showErrorDetails: false,

      // Import progress
      importProgress: 0,
      importTotal: 0,
      importSuccessCount: 0,
      importFailCount: 0,
      importErrors: [] as Array<{ row: number; title: string; error: string }>,
      showImportErrorDetails: false,
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showModal
      },
      set(newValue: boolean) {
        this.$emit('update:showModal', newValue)
      },
    },
    subtitle(): string {
      if (this.currentStep === 'select') {
        return this.projectName
          ? `Import tasks into <strong>${this.projectName}</strong>`
          : ''
      }
      if (this.currentStep === 'preview') {
        return 'Review the data below before importing.'
      }
      if (this.currentStep === 'importing') {
        return 'Import in progress...'
      }
      return 'Import complete.'
    },

    /* Custom fields can only be imported by people who could fill them in on the create form */
    editableCustomFields(): ICustomField[] {
      return this.importOptions
        ? this.importOptions.listCustomFields.filter(f => this.canEditCustomField(f))
        : []
    },

    previewRows(): ICsvTaskRow[] {
      return this.validRows.slice(0, 5)
    },

    progressPercent(): number {
      if (this.importTotal === 0) {
        return 0
      }
      return Math.round((this.importProgress / this.importTotal) * 100)
    },
  },

  watch: {
    showModal: {
      immediate: true,
      handler(isShowing: boolean) {
        if (this.currentStep === 'importing') {
          return
        }
        this.openingID++
        this.fileReadID++
        this.isPreparingExample = false
        if (!isShowing) {
          return
        }
        // Start over with the project's current fields, statuses, and people
        this.goBackToSelect()
        this.importOptions = null
        this.importOptionsRequest = null
        this.ensureImportOptions()
      },
    },
  },

  methods: {
    canEditCustomField(field: ICustomField): boolean {
      if (this.$permissions.isAdminOrOwner) {
        return true
      }
      const permission = this.$permissions.isGuest
        ? field.guestUserPermissions
        : field.normalUserPermissions
      return permission === ECustomFieldPermissions.ViewAndEdit
    },

    describeCustomFieldFormat(field: ICustomField): string {
      return CsvImportAssistant.describeCustomFieldFormat(field)
    },

    customFieldDisplayText(row: ICsvTaskRow, field: ICustomField): string {
      const value = row.customFieldValues.find(v => v.customFieldID === field.id)
      return value
        ? CustomFieldAssistant.displayText(field, { id: 0, ...value } as ICustomFieldValue)
        : ''
    },

    async loadImportOptions() {
      const openingID = this.openingID
      let options: ICsvImportOptions | null = null
      try {
        const filterOptions = await TasksAPI.get_FilterOptions(this.projectId)
        options = {
          listStatuses: filterOptions.listStatuses,
          listPriorities: filterOptions.listPriorities,
          listAssignees: filterOptions.listAssignees,
          listCustomFields: filterOptions.listCustomFields || [],
        }
      } catch {
        options = null
      }
      if (openingID === this.openingID) {
        this.importOptions = options
      }
    },

    /*
     * Waits for the project options, starting a new request if none is pending; false when they failed to load.
     * Callers still check their own openingID or fileReadID afterwards, since the modal may have reopened meanwhile.
     */
    async ensureImportOptions(): Promise<boolean> {
      if (!this.importOptionsRequest) {
        this.importOptionsRequest = this.loadImportOptions()
      }
      const request = this.importOptionsRequest
      await request
      // Let the next attempt try again, unless a newer request has replaced this one
      if (!this.importOptions && this.importOptionsRequest === request) {
        this.importOptionsRequest = null
      }
      return !!this.importOptions
    },

    handleClose() {
      if (this.currentStep === 'importing') {
        return
      }
      this.$emit('close')
    },

    handleDone() {
      if (this.importSuccessCount > 0) {
        this.$emit('import-completed')
      }
      this.$emit('close')
    },

    goBackToSelect() {
      this.fileReadID++
      this.currentStep = 'select'
      this.validRows = []
      this.customFieldColumns = []
      this.parseErrors = []
      this.parseWarnings = []
      this.structureErrors = []
      this.selectedFile = null
      this.fileInputKey++
    },

    async downloadExampleCsv() {
      // The example has a column for each custom field, so it waits for the project's fields
      const openingID = this.openingID
      this.isPreparingExample = true
      const optionsLoaded = await this.ensureImportOptions()
      if (openingID !== this.openingID) {
        return
      }
      this.isPreparingExample = false
      if (!optionsLoaded) {
        this.structureErrors = [
          'Failed to load project options. Please try again.',
        ]
        return
      }

      // Every visible field gets a column, even view-only ones: the importer pairs same-named columns with
      // fields in this order, so leaving one out would shift a column onto the wrong field
      const content = CsvImportAssistant.generateExampleCsvContent(
        this.importOptions.listCustomFields,
      )
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'import-tasks-example.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },

    handleFileSelected(event: Event) {
      const input = event.target as HTMLInputElement
      if (!input.files || input.files.length === 0) {
        return
      }

      this.selectedFile = input.files[0]
      this.structureErrors = []
      this.fileReadID++
      const fileReadID = this.fileReadID

      const reader = new FileReader()
      reader.onload = (e: any) => {
        this.processCsvText((e.target && e.target.result) as string, fileReadID)
      }

      reader.onerror = () => {
        if (fileReadID === this.fileReadID) {
          this.structureErrors = ['Failed to read the file. Please try again.']
        }
      }

      reader.readAsText(this.selectedFile)
    },

    async processCsvText(text: string, fileReadID: number) {
      // A newer file, Back, or closing the modal has replaced this file
      if (fileReadID !== this.fileReadID) {
        return
      }

      if (!text || text.trim().length === 0) {
        this.structureErrors = ['The selected file is empty.']
        return
      }

      const { headers, rows } = CsvImportAssistant.parseCsvText(text)

      if (headers.length === 0) {
        this.structureErrors = [
          'Could not parse the CSV file. Please check the format.',
        ]
        return
      }

      // Custom field columns can only be recognized once the project's fields are known
      const optionsLoaded = await this.ensureImportOptions()
      if (fileReadID !== this.fileReadID) {
        return
      }
      if (!optionsLoaded) {
        this.structureErrors = [
          'Failed to load project options. Please try again.',
        ]
        // Let the same file be picked again
        this.fileInputKey++
        return
      }

      // Validate structure
      const structureResult = CsvImportAssistant.validateCsvStructure(
        headers,
        rows,
        this.importOptions.listCustomFields,
        this.canEditCustomField,
      )
      if (!structureResult.isValid) {
        this.structureErrors = structureResult.errors
        return
      }

      if (rows.length === 0) {
        this.structureErrors = [
          'The CSV file has a header row but no data rows.',
        ]
        return
      }

      // Validate rows
      const rowResult = CsvImportAssistant.validateCsvRows(
        headers,
        rows,
        structureResult.customFieldColumns,
      )
      this.validRows = rowResult.validRows
      this.customFieldColumns = structureResult.customFieldColumns
      this.parseErrors = rowResult.errors
      this.parseWarnings = [
        ...structureResult.warnings,
        ...rowResult.warnings,
      ]

      if (this.validRows.length === 0) {
        this.structureErrors = [
          'No valid rows found. Please fix the errors and try again.',
        ]
        return
      }

      this.currentStep = 'preview'
    },

    async startImport() {
      this.currentStep = 'importing'
      this.importTotal = this.validRows.length
      this.importProgress = 0
      this.importSuccessCount = 0
      this.importFailCount = 0
      this.importErrors = []

      for (let i = 0; i < this.validRows.length; i++) {
        const row = this.validRows[i]
        // The options were loaded before the file could be previewed
        const data = CsvImportAssistant.mapCsvRowToCreateInput(
          row,
          this.importOptions,
        )
        try {
          await TasksAPI.post_Create(this.projectId, data)
          this.importSuccessCount++
        } catch (error) {
          this.importFailCount++
          const err = error as any
          this.importErrors.push({
            row: i + 1,
            title: row.title,
            error:
              (err &&
                err.response &&
                err.response.data &&
                err.response.data.message) ||
              (err && err.message) ||
              'Unknown error',
          })
        }
        this.importProgress = i + 1
      }

      this.currentStep = 'results'
    },
  },
})
</script>

<style lang="scss">
.csv-import-modal {
  .el-dialog__body {
    min-height: 500px;
  }

  &__format-info {
    margin-bottom: 20px;

    h4 {
      margin-bottom: 8px;
    }

    p {
      margin-bottom: 12px;
      color: var(--color-text-secondary);
    }
  }

  &__columns {
    display: flex;
    gap: 24px;
    margin-bottom: 16px;

    @include media-breakpoint-down(sm) {
      flex-direction: column;
      gap: 12px;
    }
  }

  &__column-group {
    flex: 1;

    strong {
      display: block;
      margin-bottom: 4px;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        padding: 2px 0;
        color: var(--color-text-secondary);
        font-size: 13px;

        code {
          background-color: var(--color-background-secondary);
          padding: 1px 4px;
          border-radius: 3px;
          font-size: 12px;
        }
      }
    }
  }

  &__download-example {
    .ddi {
      margin-right: 4px;
    }
  }

  &__file-input-area {
    margin-bottom: 16px;
  }

  &__file-label {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    border: 2px dashed $color--gray-30;
    border-radius: 6px;
    cursor: pointer;
    color: var(--color-text-secondary);
    transition: border-color 200ms, color 200ms;

    &:hover {
      border-color: $color--orange;
      color: var(--color-text-primary);
    }

    .ddi {
      font-size: 20px;
    }
  }

  &__file-hidden {
    display: none;
  }

  &__errors {
    background-color: rgba(255, 0, 0, 0.05);
    border: 1px solid rgba(255, 0, 0, 0.2);
    border-radius: 4px;
    padding: 12px 16px;

    ul {
      margin: 4px 0 0 0;
      padding-left: 18px;

      li {
        color: $color--red;
        font-size: 13px;
        padding: 2px 0;
      }
    }
  }

  &__error-title {
    color: $color--red;
    font-weight: 600;
    margin: 0;

    .ddi {
      margin-right: 4px;
    }
  }

  &__summary {
    margin-bottom: 16px;
  }

  &__summary-success {
    color: $color--green;
    font-weight: 600;
    margin-bottom: 4px;

    .ddi {
      margin-right: 4px;
    }
  }

  &__summary-warning {
    color: $color--orange;
    margin-bottom: 4px;

    .ddi {
      margin-right: 4px;
    }

    .just-a-link {
      margin-left: 4px;
    }
  }

  &__summary-error {
    color: $color--red;
    font-weight: 600;
    margin-bottom: 4px;

    .ddi {
      margin-right: 4px;
    }

    .just-a-link {
      margin-left: 4px;
    }
  }

  &__error-details {
    margin-top: 8px;
    max-height: 150px;
    overflow-y: auto;

    ul {
      margin: 0;
      padding-left: 18px;

      li {
        font-size: 13px;
        color: var(--color-text-secondary);
        padding: 2px 0;
      }
    }
  }

  &__warnings {
    margin-bottom: 12px;

    p {
      color: var(--color-text-third);
      font-size: 13px;
      font-style: italic;
    }
  }

  &__preview-table-wrapper {
    overflow-x: auto;
  }

  &__preview-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
      padding: 8px 12px;
      text-align: left;
      border-bottom: 1px solid $color--gray-30;
    }

    th {
      font-weight: 600;
      color: var(--color-text-secondary);
      white-space: nowrap;
    }

    td {
      color: var(--color-text-primary);
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__preview-more {
    color: var(--color-text-third);
    font-size: 13px;
    font-style: italic;
    margin-top: 8px;
  }

  &__importing {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
  }

  &__importing-text {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  &__progress-bar {
    width: 100%;
    max-width: 400px;
    height: 8px;
    background-color: $color--gray-30;
    border-radius: 4px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background-color: $color--orange;
    border-radius: 4px;
    transition: width 200ms ease;
  }

  &__importing-subtext {
    margin-top: 12px;
    color: var(--color-text-third);
    font-size: 13px;
  }

  &__results {
    padding: 20px 0;
  }

  &__results-summary {
    text-align: center;
  }
}
</style>
